<template>
  <v-form v-model="valid">
    <div class="mb-3">
      <span class="font-weight-bold mr-1">Step Number:</span
      ><span
        >{{ memberTestStepRecord.stepNumber
        }}{{ memberTestStepRecord.substepNumber ? `.${memberTestStepRecord.substepNumber}` : `` }}</span
      >
    </div>
    <div class="mb-3">
      <span class="font-weight-bold">Scenario: </span>
      <div v-html="memberTestStepRecord.scenario"></div>
    </div>
    <div class="mb-3">
      <label class="font-weight-bold">Testing Details</label>
      <ckeditor
        :key="`testing-details-${testStepId}`"
        :config="ckeditorMergedConfig"
        @focus="setCurrentEditor($event)"
        id="testingDetails"
        v-model="memberTestStepRecord.testingDetails"
      ></ckeditor>
    </div>
    <div class="mb-3">
      <span class="font-weight-bold">Expected Outcome: </span>
      <div v-html="memberTestStepRecord.expectedOutcome"></div>
    </div>
    <div class="mb-3">
      <label class="font-weight-bold">Actual Outcome</label>
      <ckeditor
        :key="`actual-outcome-${testStepId}`"
        :config="ckeditorMergedConfig"
        @focus="setCurrentEditor($event)"
        id="actualOutcome"
        v-model="memberTestStepRecord.actualOutcome"
      ></ckeditor>
    </div>
    <v-card-actions>
      <v-btn @click="cancel" outlined> Cancel </v-btn>
      <v-btn @click="save" :disabled="!hasTestPlanEdit" color="primary" data-cy="save-button"> Save </v-btn>
    </v-card-actions>
  </v-form>
</template>

<script>
import Ckeditor from "vue-ckeditor2";
import { cloneDeep } from "lodash";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "memberTestStepFormEdit",
  components: {
    Ckeditor,
  },
  computed: {
    ...mapGetters({
      hasTestPlanEdit: "session/hasTestPlanEdit",
    }),
    memberTestStepRecord() {
      return this.$store.state.testStep.memberTestStepRecord;
    },
    ckeditorMergedConfig() {
      // CKEditor should be read-only unless the user has the edit permission
      return Object.assign({}, this.ckeditorConfig, { readOnly: !this.hasTestPlanEdit });
    },
  },
  async created() {
    if (this.mode === "edit" && this.releaseNoteId) {
      await this.$store.dispatch("testStep/memberTestStep", {
        releaseNoteId: this.releaseNoteId,
        serviceAreaId: this.serviceAreaId,
        testStepId: this.testStepId,
      });
    }
  },
  data() {
    return {
      ckeditorConfig: {
        toolbarGroups: [
          { name: "basicstyles", groups: ["basicstyles", "cleanup"] },
          { name: "paragraph", groups: ["list", "indent", "blocks", "align", "bidi", "paragraph"] },
          { name: "links", groups: ["links"] },
          { name: "insert", groups: ["insert"] },
          "/",
          { name: "styles", groups: ["styles"] },
          { name: "colors", groups: ["colors"] },
          { name: "tools", groups: ["tools"] },
          { name: "others", groups: ["others"] },
          { name: "about", groups: ["about"] },
          { name: "editing", groups: ["find", "selection", "editing"] },
          { name: "forms", groups: ["forms"] },
          { name: "document", groups: ["mode", "document", "doctools"] },
          { name: "clipboard", groups: ["clipboard", "undo"] },
        ],
        removeButtons:
          "Flash,Iframe,PageBreak,Language,About,Subscript,Superscript,CopyFormatting,Anchor,Find,Replace,SelectAll,Cut,Copy,PasteFromWord,Paste,Templates,Save,NewPage,Preview,Print,Form,Checkbox,Radio,TextField,Textarea,Select,Button,ImageButton,HiddenField",
        height: 200,
        versionCheck: false,
        readOnly: false,
      },
      currentEditor: null,
      valid: true,
    };
  },
  watch: {
    // Watch for route param changes to reload data when navigating between test steps
    // This prevents race conditions where stale data from previous test step is displayed
    testStepId(newId, oldId) {
      if (this.isValidId(newId) && newId !== oldId && this.mode === "edit" && this.isValidId(this.releaseNoteId)) {
        this.$store.dispatch("testStep/memberTestStep", {
          releaseNoteId: this.releaseNoteId,
          serviceAreaId: this.serviceAreaId,
          testStepId: newId,
        });
      }
    },
    releaseNoteId(newId, oldId) {
      if (this.isValidId(newId) && newId !== oldId && this.mode === "edit" && this.isValidId(this.testStepId)) {
        this.$store.dispatch("testStep/memberTestStep", {
          releaseNoteId: newId,
          serviceAreaId: this.serviceAreaId,
          testStepId: this.testStepId,
        });
      }
    },
  },
  methods: {
    cancel() {
      this.$router.go(-1);
    },
    isValidId(id) {
      return typeof id === "number" && Number.isInteger(id) && id > 0;
    },
    registerEventListener(editor) {
      const editableContent = editor.editable();
      editableContent.attachListener(editableContent, "blur", e => {
        if (editableContent.getData() !== this.memberTestStepRecord[editor.name]) {
          this.memberTestStepRecord[editor.name] = editableContent.getData();
        }
      });
    },
    async save() {
      if (this.mode === "edit") {
        const data = cloneDeep(this.memberTestStepRecord);

        ["actualOutcome", "testingDetails"].forEach(field => {
          if (typeof data[field] === "string" && data[field].trim() === "") {
            data[field] = null;
          }
        });

        try {
          await this.updateMemberStep(data);
          this.$notifySuccess("Successfully saved test step details.");
          this.$router.push({
            name: this.redirectionPath,
            params: this.$route.params,
            query: {
              tab: "test-steps",
            },
          });
        } catch (e) {
          console.error(e);
          this.$notifyError("There was an error saving test step details.");
        }
      }
    },
    setCurrentEditor({ editor }) {
      this.currentEditor = editor.name;
      this.registerEventListener(editor);
    },
    ...mapActions({
      updateMemberStep: "testStep/updateMemberStep",
    }),
  },
  props: {
    mode: {
      type: String,
      default: "create",
    },
    redirectionPath: {
      type: String,
    },
    releaseNoteId: {
      type: Number,
      validator: value => Number.isInteger(value) && value > 0,
    },
    serviceAreaId: {
      type: Number,
      validator: value => Number.isInteger(value) && value > 0,
    },
    testStepId: {
      type: Number,
      validator: value => Number.isInteger(value) && value > 0,
    },
  },
};
</script>
